import { useCallback, useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import {
  useGetSpendByDimension,
  type GetSpendByDimensionParams,
} from "@workspace/api-client-react";
import { EquityPanel } from "@/components/analytics/EquityPanel";
import {
  FilterBar,
  SavedViewsControls,
  filtersToSearchParams,
  searchParamsToFilters,
  type Filters,
} from "@/pages/analytics";
import { useOrg } from "@/lib/orgContext";

export default function EquityPage() {
  const { org } = useOrg();
  const search = useSearch();
  const [, setLocation] = useLocation();

  const filters: Filters = useMemo(() => searchParamsToFilters(search), [search]);
  const setFilters = useCallback(
    (f: Filters) => {
      setLocation(`/equity${filtersToSearchParams(f)}`, { replace: true });
    },
    [setLocation],
  );

  const isAdmin = org?.role === "admin";

  // Derive available sports/roles from spend data so the filter bar matches Analytics.
  const spendParams: GetSpendByDimensionParams = {
    fiscalYearStart: filters.fiscalYearStart,
    fiscalYearEnd: filters.fiscalYearEnd,
    sport: filters.sport,
    role: filters.role,
    status: filters.status as GetSpendByDimensionParams["status"],
    groupBy: "sport",
  };
  const { data: spendBySport } = useGetSpendByDimension(spendParams);
  const { data: spendByRole } = useGetSpendByDimension({ ...spendParams, groupBy: "role" });
  const sports = useMemo(() => (spendBySport ?? []).map((r) => r.dimension), [spendBySport]);
  const roles = useMemo(() => (spendByRole ?? []).map((r) => r.dimension), [spendByRole]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          <h1>Equity</h1>
          <p>Title IX equity reporting: men&apos;s vs women&apos;s spend, comparable pairs, and proportionality</p>
        </div>
      </div>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        sports={sports}
        roles={roles}
        presetsSlot={<SavedViewsControls filters={filters} onLoad={setFilters} />}
      />

      <EquityPanel
        isAdmin={!!isAdmin}
        sport={filters.sport ?? undefined}
        initialFiscalYear={filters.fiscalYearStart ?? new Date().getFullYear()}
        status={filters.status ?? undefined}
        role={filters.role ?? undefined}
      />
    </div>
  );
}
